<template>
  <div class="block">
    <div :class="['box is-paddingless', $style.userListContainer]">
      <table class="table is-fullwidth">
        <thead>
          <tr>
            <th width="35%">
              Project Name
              <b-icon
                v-if="
                  filteredParams.orderBy.field === 'title' &&
                  filteredParams.orderBy.sort === 'DESC'
                "
                icon="arrow-up"
                size="is-small"
                :class="$style.orderBy"
                @click.native="updateSort('title', 'ASC')"
              ></b-icon>
              <b-icon
                v-else
                icon="arrow-down"
                size="is-small"
                :class="$style.orderBy"
                @click.native="updateSort('title', 'DESC')"
              ></b-icon>
            </th>
            <th width="15%">Project Status</th>
            <th width="30%">
              Administrator
              <b-icon
                v-if="
                  filteredParams.orderBy.field === 'administrator' &&
                  filteredParams.orderBy.sort === 'DESC'
                "
                icon="arrow-up"
                size="is-small"
                :class="$style.orderBy"
                @click.native="updateSort('administrator', 'ASC')"
              ></b-icon>
              <b-icon
                v-else
                icon="arrow-down"
                size="is-small"
                :class="$style.orderBy"
                @click.native="updateSort('administrator', 'DESC')"
              ></b-icon>
            </th>
            <th width="15%">Administrator Status</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="item in items" :key="item.id">
            <td :class="$style.projectTitleCell">{{ item.title || '-' }}</td>
            <td>{{ item.status || '-' }}</td>
            <td :class="$style.projectAdminCell">
              <div v-if="item.administratorName.length > 50">
                <b-tooltip
                  :label="item.administratorName"
                  position="is-top"
                  size="is-small"
                >
                  <p
                    v-if="item.given_name.length + item.family_name.length > 35"
                  >
                    <b>{{ truncateAdministratorName(item, true) }}</b>
                  </p>
                  <p v-else>
                    <b>
                      {{ item.given_name }}
                    </b>
                    <b>
                      {{ item.family_name }}
                    </b>
                    ({{ truncateAdministratorName(item, false) }})
                  </p>
                </b-tooltip>
              </div>
              <div v-else>
                <p :class="$style.projectAdminNameCell">
                  {{ item.given_name }} {{ item.family_name }}
                </p>
                <p :class="$style.projectAdminEmailCell">({{ item.email }})</p>
              </div>
            </td>
            <td>{{ getSubscriptionStatus(item) || '-' }}</td>
            <td>
              <template>
                <TableDropdown>
                  <b-dropdown-item
                    aria-role="listitem"
                    @click="handleChangeProjectAdministrator(item)"
                    :disabled="item.disableChangeAdministration"
                    :title="
                      item.disableChangeAdministration
                        ? unAvailableParticipantTooltip
                        : undefined
                    "
                  >
                    Change Project Administrator
                  </b-dropdown-item>
                  <b-dropdown-item
                    aria-role="listitem"
                    @click="handleProjectOwnerHistory(item)"
                    :disabled="item.disableProjectHistory"
                  >
                    View Project Ownership History
                  </b-dropdown-item>
                </TableDropdown>
              </template>
            </td>
          </tr>
        </tbody>

        <tfoot v-if="items && items.length === 0">
          <tr>
            <td colspan="100%" class="is-paddingless">
              <p class="p-2 has-text-centered">No Data Found</p>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
    <BasePagination
      v-if="items && items.length > 0"
      :page.sync="page"
      :perPage.sync="perPage"
      :totalNumberOfPage="totalNumberOfPage"
      :totalCount="totalCount"
      :isFirstPage="isFirstPage"
      :isLastPage="isLastPage"
      :startItemIndex="startItemIndex"
      :endItemIndex="endItemIndex"
    />
  </div>
</template>

<script lang="ts">
import { Component, Prop, Watch } from 'vue-property-decorator';
import { State, Action } from 'vuex-class';
import BasePagination from '@/components/base/BasePagination.vue';
import BaseModal from '@/jbi-shared/vue-components/BaseModal.vue';
import { RootState } from '@/store/store';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import { FilteredUserPayload } from '@/store/modules/users/types/users.types';
import { mixins } from 'vue-class-component';
import { PaginationMixin } from '@/components/base/pagination.mixin';
import { ToastProgrammatic as Toast } from 'buefy';
import dayjs from 'dayjs';
import ProjectOwnerRequestForm from '../../Project/components/ProjectOwnerRequestForm.vue';
import {
  ProjectOwnerHistoryPayload,
  AdminProjectDTO,
  ChangeProjectOwnerDTO,
} from '@/store/modules/projects/types/projects.types';
import TableDropdown from './TableDropdown.vue';
import ProjectOwnerHistory from './ProjectOwnerHistory.vue';
import { useUserEmail } from '@/utils/user.util';
import { cloneData } from '@/utils/general.util';

@Component({
  components: {
    BaseModal,
    BasePagination,
    TableDropdown,
  },
})
export default class ProjectList extends mixins(PaginationMixin) {
  @Prop() public items!: AdminProjectDTO[];
  @Prop(Number) public currentPage!: number;
  @Prop(Number) public totalNumberOfPage!: number;
  @Prop(Number) public totalCount!: number;
  @Prop(Boolean) public isFirstPage!: boolean;
  @Prop(Boolean) public isLastPage!: boolean;
  @Prop(Number) public startItemIndex!: number;
  @Prop(Number) public endItemIndex!: number;
  @Prop() public filteredParams!: FilteredUserPayload;

  public projectOwnerHistoryLog: any = [];
  public unAvailableParticipantTooltip =
    'More than one participant is required' +
    '\n to change the project administrator.';

  @Action('projects/getProjectOwnerHistory')
  public getProjectOwnerHistory!: (projectId: number) => void;

  @State((state: RootState) => state.projects.projectOwnerHistory)
  public projectOwnerHistory!: ProjectOwnerHistoryPayload;

  get userEmail(): string | undefined {
    return useUserEmail.call(this);
  }

  public updateSort(field: string, sort: string) {
    if (this.filteredParams.orderBy) {
      this.filteredParams.orderBy.field = field;
      this.filteredParams.orderBy.sort = sort;
    }
  }

  public truncateAdministratorName(
    item: AdminProjectDTO,
    isNameLonger: boolean,
  ) {
    const administratorName = item.administratorName.slice(
      0,
      -(item.email.length + 3),
    );
    if (isNameLonger) {
      return `${administratorName.substring(0, 35)} ...`;
    } else {
      return `${item.email.substring(0, 40 - administratorName.length)} ...`;
    }
  }

  public getSubscriptionStatus(data: AdminProjectDTO) {
    let subscriptionStatus: string = '';
    const remainingDays = dayjs(data.expire_at).diff(new Date(), 'day');
    if (remainingDays <= 7) {
      subscriptionStatus =
        remainingDays <= 0 ? `Expired` : `Expiring (${remainingDays} days)`;
    } else {
      subscriptionStatus = 'Active';
    }
    return subscriptionStatus;
  }

  // public projectOwnerEmail(item: any) {
  //   if (item.email === this.userEmail) {
  //     return 'You';
  //   } else {
  //     return item.email;
  //   }
  // }

  public handleChangeProjectAdministrator(item: AdminProjectDTO) {
    this.$buefy.modal.open({
      parent: this,
      props: {
        projectId: item.id,
        projectOwnerEmail: item.email,
        projectOwnerName: item.administratorName,
        isSystemAdmin: true,
      },
      events: {
        confirm: (newValue: ChangeProjectOwnerDTO) => {
          let confirmMessage: string = '';
          if (newValue.isAdminNewProjectOwner) {
            confirmMessage = `You are now the project administrator in ‘${item.title}'`;
          } else {
            confirmMessage = `Project administration request successfully sent to ${newValue.newRequestedProjectOwnerEmail}`;
          }
          Toast.open({
            message: confirmMessage,
            position: 'is-top',
            type: 'is-dark',
            duration: 3500,
          });
          this.$emit('updateProjectList', newValue.isAdminNewProjectOwner);
        },
      },
      component: ProjectOwnerRequestForm,
      hasModalCard: true,
    });
  }

  public handleProjectOwnerHistory(item: AdminProjectDTO) {
    this.getProjectOwnerHistory(item.id);
  }

  @Watch('projectOwnerHistory')
  @isTruthy
  public projectOwnerHistoryState(newValue: any) {
    this.projectOwnerHistoryLog = [];
    if (newValue.isOriginalProjectOwner) {
      this.projectOwnerHistoryLog.push({
        originalProjectOwnerName: newValue.originalProjectOwner,
        eventOccuredAt: newValue.dateProjectOwnerCreated,
        isOriginalProjectOwner: true,
      });
    } else {
      this.projectOwnerHistoryLog = cloneData(newValue);
      this.projectOwnerHistoryLog.push({
        originalProjectOwnerName: newValue[0].originalProjectOwner,
        eventOccuredAt: newValue[0].dateProjectOwnerCreated,
        isOriginalProjectOwner: true,
      });
    }
    this.$buefy.modal.open({
      parent: this,
      props: {
        projectOwnerHistoryLog: this.projectOwnerHistoryLog,
      },
      component: ProjectOwnerHistory,
      hasModalCard: true,
    });
  }
}
</script>

<style lang="scss" module>
.userListContainer {
  border-radius: 6px;
  overflow: visible;
  & > table {
    table-layout: fixed;
    thead {
      background-color: #ffffff;
      tr {
        font-size: 14px;
        th {
          color: #9f9f9f;
          font-weight: initial !important;
          padding: 20px 20px 23px 12px;
        }
        th:first-child {
          padding-left: 20px;
        }
      }
    }
    tbody {
      background-color: #ffffff;
      tr {
        td {
          color: #444f51;
          border: 0.5px solid #dbdbdb;
          border-width: 0 0 0.5px;
          padding-top: 20px;
          padding-bottom: 18px;
          padding-right: 0px;
        }
        td:first-child {
          padding-left: 20px;
        }
      }
    }
  }
}
.orderBy {
  cursor: pointer;
}
.dropdown :global {
  .dropdown-menu {
    .dropdown-content {
      padding: 0;

      .dropdown-item {
        border-bottom: 1px solid lightgrey;
        font-size: 11px;

        &:hover,
        label {
          background: #f7fbff;
          cursor: pointer;
        }
      }
    }
  }
}
.projectTitleCell {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.projectAdminCell {
  width: 50%;
  overflow: visible;
}
.projectAdminNameCell {
  font-weight: 700;
  display: inline-block;
}
.projectAdminEmailCell {
  display: inline-block;
  padding-left: 5px;
}
.tooltipTitle {
  font-family: Oswald;
  font-size: 40px;
  font-weight: 700;
  color: #00205b;
  &::after {
    font-family: 'Open Sans';
  }
}
</style>
